import { useState } from 'react';
import { usePageMeta, useSchemaOrg } from '@/hooks/usePageMeta';
import PageShell from '@/components/feature/PageShell';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { NEWSLETTER_FORM_URL } from '@/utils/forms';

const timeline = [
  {
    year: '2016',
    title: '品牌创立',
    desc: '在江苏无锡阳山创立美丽的小蜜桃品牌，坚持"只做优质产区精品桃"的理念，开启精品蜜桃事业。',
  },
  {
    year: '2018',
    title: '拓展产区',
    desc: '与四川龙泉驿、浙江奉化优质桃园建立深度合作，实现三大核心产区全覆盖，丰富产品线。',
  },
  {
    year: '2020',
    title: '冷链升级',
    desc: '全面升级冷链物流体系，与顺丰冷链达成战略合作，确保蜜桃从枝头到餐桌仅需24-48小时。',
  },
  {
    year: '2022',
    title: '品质认证',
    desc: '通过国家绿色食品认证，年销售量突破300吨，累计服务超过5万名客户，好评率达99.5%。',
  },
  {
    year: '2024',
    title: '品牌升级',
    desc: '全新品牌形象上线，推出时令限定系列产品，打造"送礼就送小蜜桃"的品牌心智。',
  },
  {
    year: '2026',
    title: '全国布局',
    desc: '年销量突破500吨，服务客户超10万，成为国内精品水蜜桃电商领域领先品牌。',
  },
];

const testimonials = [
  {
    name: '王女士',
    location: '北京',
    rating: 5,
    text: '已经是第三次购买了！水蜜桃品质非常好，个头大、水分足、甜度恰到好处。送了一盒给爸妈，他们都说从来没吃过这么好吃的桃子。会一直回购！',
    date: '2026-07-28',
  },
  {
    name: '李先生',
    location: '上海',
    rating: 5,
    text: '收到的时候包装非常仔细，每颗桃子都有独立保护。打开盒子就能闻到浓郁的桃香，放了两天更软更甜。这个品质完全对得起这个价格。',
    date: '2026-07-25',
  },
  {
    name: '张女士',
    location: '广州',
    rating: 5,
    text: '买了好几家的水蜜桃对比，美丽的小蜜桃确实是最好的！果肉细腻、汁水丰沛，而且冷链很到位，广州这么热的天收到还是冰凉的。已经推荐给好几位朋友了。',
    date: '2026-07-22',
  },
  {
    name: '赵先生',
    location: '深圳',
    rating: 5,
    text: '第一次尝试网购生鲜水果，体验远超预期。桃子个头非常均匀，颜色粉嫩漂亮，口感鲜嫩多汁。包装精美大方，送礼很有面子。',
    date: '2026-07-20',
  },
  {
    name: '陈女士',
    location: '杭州',
    rating: 4,
    text: '礼盒装非常精美，送给了合作伙伴，对方很满意。唯一的建议是如果能提供定制贺卡服务就更好了。整体来说非常推荐！',
    date: '2026-07-18',
  },
  {
    name: '刘先生',
    location: '成都',
    rating: 5,
    text: '老家就是龙泉驿的，对水蜜桃非常挑剔。买了一份龙泉驿水蜜桃尝鲜装，品质出乎意料的好，果肉细腻程度比我记忆中的还好。支持良心商家！',
    date: '2026-07-15',
  },
];

export default function About() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactMsg, setContactMsg] = useState('');

  usePageMeta({
    title: '关于我们 - 品牌故事 | 口碑评价 | 联系方式 | 美丽的小蜜桃',
    description: '美丽的小蜜桃品牌介绍：2016年创立，精选阳山、龙泉驿、奉化三大核心产区优质水蜜桃。品质认证、冷链直达、口碑卓越。联系我们了解更多新鲜蜜桃礼盒信息。',
    keywords: '美丽的小蜜桃品牌,水蜜桃品牌介绍,精品桃子品牌,蜜桃口碑,水蜜桃联系方式',
    canonicalUrl: 'https://meilixiaomitao.com/about',
  });

  useSchemaOrg({
    organization: true,
    website: true,
    webpage: true,
    breadcrumb: [
      { name: '首页', url: 'https://meilixiaomitao.com/' },
      { name: '关于我们', url: 'https://meilixiaomitao.com/about' },
    ],
  });

  const handleContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setContactStatus('success');
      setContactMsg('提交成功！我们会尽快与您联系。');
      setTimeout(() => setContactStatus('idle'), 3000);
      return;
    }

    setContactStatus('loading');
    try {
      const formData = new FormData(form);
      const resp = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (resp.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactMsg('提交成功！感谢您的留言，我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        setContactStatus('error');
        setContactMsg(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setContactStatus('error');
      setContactMsg('网络异常，请检查网络后重试。');
    }
    setTimeout(() => setContactStatus('idle'), 4000);
  };

  return (
    <PageShell>
      <div className="pt-24 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="text-center mb-16">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              关于美丽的小蜜桃
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              美丽的小蜜桃，精选优质产区水蜜桃，坚持品质至上，让每一颗蜜桃都成为大自然的甜蜜馈赠。
              从枝头到餐桌，我们用心守护每一份新鲜。
            </p>
          </section>

          {/* Brand Story - Timeline */}
          <section id="story" className="mb-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-12">
              品牌发展历程
            </h2>
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-8">
                  {timeline.map((item, index) => (
                    <div
                      key={item.year}
                      className={`flex flex-col md:flex-row gap-4 md:gap-8 items-start ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                        {index % 2 === 0 ? (
                          <div className="bg-background-50 rounded-lg border border-background-200 p-5">
                            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                            <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3 md:block md:text-center flex-shrink-0">
                        <div className="w-8 h-8 md:mx-auto flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                          <span className="hidden md:inline">{item.year}</span>
                          <span className="md:hidden text-[10px]">{item.year.slice(2)}</span>
                        </div>
                        <span className="md:hidden text-sm font-semibold text-foreground-900">{item.title}</span>
                      </div>
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-left' : 'md:text-right'}`}>
                        <div className="bg-background-50 rounded-lg border border-background-200 p-5">
                          <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="mb-20 bg-background-100 rounded-2xl py-14 px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-10">
              我们的坚持
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { icon: 'ri-leaf-line', title: '源头直采', desc: '深入核心产区，与桃农建立长期合作，从源头把控品质，减少中间环节。' },
                { icon: 'ri-eye-line', title: '严格筛选', desc: '每颗桃子经过5道筛选工序，果形、色泽、成熟度、重量、甜度全面把关。' },
                { icon: 'ri-truck-line', title: '冷链直达', desc: '清晨采摘，当日冷链发出，全程温控运输，确保蜜桃以最佳新鲜度到达。' },
                { icon: 'ri-heart-line', title: '品质承诺', desc: '坏果包赔、不满意退。我们对自己的产品有信心，更对您的体验负责。' },
                { icon: 'ri-earth-line', title: '绿色种植', desc: '坚持无公害种植方式，不使用催熟剂和保鲜剂，守护自然本味。' },
                { icon: 'ri-team-line', title: '用心服务', desc: '专业客服团队随时响应，用心解答每一个问题，让您购物无忧。' },
              ].map((v) => (
                <div key={v.title} className="text-center p-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-100">
                    <i className={`${v.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section id="reputation" className="mb-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-4">
              客户口碑
            </h2>
            <p className="text-center text-foreground-500 text-sm mb-10">
              来自全国各地的真实评价，感谢每一位客户的信任与支持
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200 p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <div key={s} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-xs ${s < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.text}"
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{t.name} · {t.location}</span>
                    <span>{t.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-center text-foreground-500 text-sm mb-10">
              有任何问题或合作意向，欢迎随时与我们联系
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {[
                { icon: 'ri-phone-line', title: '客服电话', content: '400-888-6666', sub: '工作日 9:00-18:00' },
                { icon: 'ri-mail-line', title: '电子邮箱', content: 'hello@meilixiaomitao.com', sub: '24小时内回复' },
                { icon: 'ri-map-pin-line', title: '公司地址', content: '江苏省无锡市阳山镇', sub: '蜜桃产业园区' },
              ].map((c) => (
                <div key={c.title} className="bg-background-50 rounded-lg border border-background-200 p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                    <i className={`${c.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">{c.title}</h3>
                  <p className="text-sm text-foreground-700 font-medium mb-0.5">{c.content}</p>
                  <p className="text-xs text-foreground-400">{c.sub}</p>
                </div>
              ))}
            </div>

            {contactStatus === 'success' ? (
              <div className="bg-green-50 border border-green-200 text-green-700 px-5 py-4 rounded-lg text-sm text-center">
                {contactMsg}
              </div>
            ) : contactStatus === 'error' ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-lg text-sm text-center">
                {contactMsg}
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleContact} className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contactName" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contactName"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contactEmail" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contactEmail"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电话
                  </label>
                  <input
                    id="contactPhone"
                    name="phone"
                    type="tel"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的电话号码"
                  />
                </div>
                <div className="mb-5">
                  <label htmlFor="contactMessage" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contactMessage"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您想咨询的内容（限500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={contactStatus === 'loading'}
                  className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer disabled:opacity-60"
                >
                  {contactStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>
    </PageShell>
  );
}